import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PMDataTable } from "../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../utils/PMTableView/CellLink";
import { PMPagination } from "../../utils/PMPagination";
import { useSettings } from "../../../contexts/settings-context";
import { OrderStatus, orderStatusOptions } from "./utils";
import { CustomerTitle, ProjectTitle } from "../../Utils";
import { TripletexSyncBadge } from "../tripletex/tripletex-sync-badge";

const statusLabel = (value) => {
  if (!value) return null;
  const match = orderStatusOptions.find(
    (o) => String(o.value).toLowerCase() === String(value).toLowerCase()
  );
  return match ? match.label : value;
};

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export const getOrderColumns = (t, priceFormatter) => [
  {
    key: "order_number",
    label: t("order.number"),
    sortKey: "order_number",
    width: 150,
    always: true,
    cell: (item) => (
      <div className="flex flex-col gap-0.5">
        <CellLink
          to={item?.id ? `/dashboard/orders/${item.id}` : null}
          className="text-[13.5px] font-semibold tabular-nums text-blue-600"
        >
          {item.order_number}
        </CellLink>
        {item.order_date && (
          <span className="text-[12px] tabular-nums text-slate-400">{item.order_date}</span>
        )}
      </div>
    ),
  },
  {
    key: "customer",
    label: t("customer"),
    sortKey: "customer",
    width: 240,
    groupable: true,
    groupValue: (item) => item?.customer?.name || item?.customer?.displayTitle || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <CellLink
          to={item?.customer?.id ? `/dashboard/customers/${item.customer.id}` : null}
          className="truncate text-[13.5px] font-medium text-slate-800"
        >
          <CustomerTitle customer={item?.customer} />
        </CellLink>
        {item?.project && (
          <span className="truncate text-[12px] text-slate-400">
            <ProjectTitle project={item?.project} />
          </span>
        )}
      </div>
    ),
  },
  {
    key: "status",
    label: t("status"),
    sortKey: "status",
    width: 150,
    stopRowClick: true,
    groupable: true,
    groupValue: (item) => item?.status || "",
    groupLabel: (key) => statusLabel(key) || t("table_view.no_value"),
    cell: (item) => <OrderStatus order={item} />,
  },
  {
    key: "total_amount",
    label: t("order.total"),
    sortKey: "total_amount",
    width: 130,
    align: "right",
    cell: (item) => (
      <span className="tabular-nums text-[13.5px] text-slate-700">
        {priceFormatter ? priceFormatter.format(item?.total_amount || 0) : item?.total_amount}
      </span>
    ),
  },
  {
    key: "tripletex_sync",
    label: t("tripletex_sync.badge.column"),
    width: 130,
    defaultVisible: true,
    stopRowClick: true,
    cell: (item) => <TripletexSyncBadge sync={item?.tripletex_sync} />,
  },
];

export const OrdersTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  // Legacy (offers/templates) pagination support
  page,
  onPageChange,
  perPage,
  itemsCount,
  currentCount,
  layout = "table",
  hideSelection = false,
}) => {
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();
  const { data = [], isLoading } = itemState;

  // When the page doesn't supply a column schema (e.g. offer templates) build
  // a default one and run in legacy paginated mode.
  const legacy = !columns;
  const resolvedColumns = useMemo(
    () => columns || getOrderColumns(t, priceFormatter),
    [columns, t, priceFormatter]
  );

  return (
    <>
      <PMDataTable
        columns={resolvedColumns}
        visibleKeys={visibleKeys}
        widths={widths}
        onResize={onResize}
        groupBy={groupBy}
        sort={sort}
        onSort={onSort}
        data={data}
        isLoading={isLoading}
        density={density}
        hasMore={legacy ? false : hasMore}
        onLoadMore={legacy ? undefined : onLoadMore}
        onSelectionChange={onItemsSelect}
        onRowClick={
          onRowClick || (legacy ? (order) => navigate(`/dashboard/orders/${order.id}`) : undefined)
        }
        layout={layout}
        hideSelection={hideSelection}
      />
      {legacy && onPageChange && (
        <PMPagination
          disabled={isLoading}
          onPageChange={onPageChange}
          page={page}
          perPage={perPage}
          rowsCount={itemsCount}
          totalCount={itemsCount}
          currentCount={currentCount}
          showCount={true}
        />
      )}
    </>
  );
};

OrdersTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};

export default OrdersTable;
